// hooks/useWebSocket.js
import { useState, useRef, useCallback } from 'react';
import { WS_BASE, getAuthToken, normalizeImageUrl } from '../utils/constants';

export const useWebSocket = (onMessageReceived) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState(null);
  const [onlineCount, setOnlineCount] = useState(0);
  
  const wsRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    inactivityTimerRef.current = setTimeout(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        setDisconnectReason('因超過15分鐘不活躍已斷開連線');
        wsRef.current.close(1000, 'inactive');
      }
    }, 15 * 60 * 1000);
  }, []);

  const startHeartbeat = useCallback(() => {
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'heartbeat' }));
      }
    }, 30000);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'new_message':
        if (data.data.images && data.data.images.length > 0) {
          data.data.images = data.data.images.map(normalizeImageUrl);
        }
        onMessageReceived('new_message', data.data);
        break;
        
      case 'user_join':
      case 'user_leave':
      case 'online_count_update':
        setOnlineCount(data.data.online_count);
        break;
        
      case 'mention_notification':
        onMessageReceived('mention_notification', data.data);
        if (Notification.permission === 'granted') {
          new Notification(`${data.data.from_user} 提到了你`, {
            body: data.data.message_text,
            icon: '/favicon.ico'
          });
        }
        break;

      case 'force_disconnect':
        if (data.data.reason === 'inactive') {
          setDisconnectReason('因超過15分鐘不活躍已斷開連線');
        } else {
          setDisconnectReason(data.data.message || '伺服器強制斷開連線');
        }
        break;

      case 'heartbeat_response':
        console.log('心跳響應已收到');
        break;
        
      case 'error':
        console.error('伺服器錯誤:', data.data.message);
        break;
    }
  }, [onMessageReceived]);

  const connect = useCallback(() => {
    const token = getAuthToken();
    if (!token) return;
    
    setIsConnecting(true);
    setDisconnectReason(null);
    
    const wsUrl = `${WS_BASE}/ws/chat?token=${token}`;
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onopen = () => {
      console.log('聊天室WebSocket連線已建立');
      setIsConnected(true);
      setIsConnecting(false);
      resetInactivityTimer();
      startHeartbeat();
    };
    
    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
        resetInactivityTimer();
      } catch (error) {
        console.error('解析WebSocket消息失敗:', error);
      }
    };
    
    wsRef.current.onclose = (event) => {
      console.log('WebSocket連線已關閉', event.code, event.reason);
      setIsConnected(false);
      setIsConnecting(false);
      stopHeartbeat();
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      if (event.reason === 'inactive') {
        setDisconnectReason('因超過15分鐘不活躍已斷開連線');
      } else if (event.code === 1001) {
        setDisconnectReason('伺服器正在重啟');
      } else if (event.code === 1006) {
        setDisconnectReason('網路連線異常');
      } else if (event.code === 4001) {
        setDisconnectReason('請不要重複連線');
      } else if (event.code !== 1000) {
        setDisconnectReason('連線意外中斷');
      }
    };
    
    wsRef.current.onerror = (error) => {
      console.error('WebSocket錯誤:', error);
      setIsConnected(false);
      setIsConnecting(false);
      setDisconnectReason('連線發生錯誤');
    };
  }, [resetInactivityTimer, startHeartbeat, stopHeartbeat, handleWebSocketMessage]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'user_disconnect');
    }
    setDisconnectReason(null);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      resetInactivityTimer();
      return true;
    }
    return false;
  }, [resetInactivityTimer]);

  const cleanup = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    stopHeartbeat();
  }, [stopHeartbeat]);

  return {
    isConnected,
    isConnecting,
    disconnectReason,
    onlineCount,
    connect,
    disconnect,
    sendMessage,
    cleanup
  };
};